import SuperstoreIDB from './indexeddb';
import SuperstoreLocal from './localstorage';

type SuperstoreBackendFactory = typeof SuperstoreIDB | typeof SuperstoreLocal;
type Superstore = ReturnType<SuperstoreBackendFactory>;

export type SuperstoreOptions = {
  /** A name to group all stored data by and to refer to from other apps. */
  namespace: string;
  /** True if the data should be stored across all portals. */
  global?: boolean;

  /** A function that returns a partition fragment to be used instead of portal ID in the key name. If omitted, PortalIDParser.get is used. */
  partition?: Function;
};

const defaultOpts = {
  async: true,
};

interface LegacySuperstoreOptions extends SuperstoreOptions {
  backend: SuperstoreBackendFactory;
  async?: boolean;
}

function validateOptions(opts: SuperstoreOptions) {
  if (!opts || !opts.namespace) {
    throw new Error(
      'Configuration error: Superstore must be instantiated with a namespace.'
    );
  } else {
    if (typeof opts.namespace !== 'string') {
      throw new Error(
        'Configuration error: `namespace` must be of type string.'
      );
    }
  }

  if (opts.global) {
    if (opts.partition) {
      throw new Error(
        'Configuration error: `partition` and `global` are mutually exclusive.'
      );
    }
  } else {
    if (opts.partition) {
      if (typeof opts.partition !== 'function') {
        throw new Error('Configuration error: `partition` must be a function.');
      }
    }
  }
}

/**
 * Interface for creating a Superstore instance. Most interesting things happen in the backends,
 * this class is mostly just for parameters validation.
 *
 *```js
 * import Superstore, { LocalStorage } from 'superstore';
 *
 * const store = new Superstore({
 *   backend: LocalStorage,
 *   namespace: 'foo'
 * });
 *```
 */
const Superstore = function (opts: LegacySuperstoreOptions) {
  if (!opts || !opts.backend) {
    throw new Error(
      'Configuration error: Superstore must be instantiated with a backend.'
    );
  }
  validateOptions(opts);
  return opts.backend({
    ...Superstore._defaultOpts,
    ...opts,
  } as any);
} as {
  _defaultOpts: Partial<LegacySuperstoreOptions>;
  /** In TypeScript, use the named factory function for your target backend */
  new (opts: LegacySuperstoreOptions): unknown;
  /** In TypeScript, use the named factory function for your target backend */
  (opts: LegacySuperstoreOptions): unknown;
};

Superstore._defaultOpts = defaultOpts;

export default Superstore;

export function createAsyncLocalSuperstore(opts: SuperstoreOptions) {
  validateOptions(opts);
  return SuperstoreLocal({ ...opts, async: true });
}

export function createSyncLocalSuperstore(opts: SuperstoreOptions) {
  validateOptions(opts);
  return SuperstoreLocal({ ...opts, async: false });
}

export function createIndexedDBSuperstore(opts: SuperstoreOptions) {
  validateOptions(opts);
  return SuperstoreIDB({ ...opts, async: true });
}
